import React, { useState, useEffect } from "react"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { Link } from "gatsby"
import { trackCustomEvent } from "gatsby-plugin-google-analytics"

import * as l from "../SideBar.module.scss"
import * as s from "./Cart.module.scss"

import { initializeCheckout, updateLineItem } from "@ducks/cartSlice"
import { setState } from "@ducks/sidebarSlice"

import LeafImage from "@assets/images/leaf.png"
import CartIcon from "@assets/svg/shoppingCart.inline.svg"

import { formatShopifyPrice } from "@utils/formatShopifyPrice"
import isFreeShipping from "@utils/isFreeShipping"
import freeShippingAmount from "@utils/freeShippingAmount"
import toFixedAmount from "@utils/toFixedAmount"

import { Cta } from "@components/common/cta/Cta"
import { QuantitySelector } from "@components/main/quantity-selector/QuantitySelector"
import { CheckBox } from "@components/common/form/CheckBox"

const Cart = () => {
  const dispatch = useDispatch()
  const checkout = useSelector(state => state.cart.checkout)
  const checkoutTransition = useSelector(
    state => state.sidebar.checkoutTransition,
  )
  const showUpSell = useSelector(state => state.cart.upsellIsVisible)

  const [isOver18, setIsOver18] = useState(false)
  const [isOver18Error, setIsOver18Error] = useState(false)

  const openUpSell = () => {
    if (!isOver18) {
      setIsOver18Error(true)
      return
    } else {
      setIsOver18Error(false)
    }

    dispatch(
      setState({
        upsellIsVisible: true,
      }),
    )
  }

  useEffect(() => {
    dispatch(initializeCheckout())
  }, [])

  useEffect(() => {
    if (isOver18) {
      setIsOver18Error(false)
    }
  }, [isOver18])

  return (
    <>
      <div className={l.Content__header}>
        <h3>Your Cart</h3>
      </div>
      {checkout && (
        <div className={`${s.Cart} ${showUpSell ? s.Cart___locked : ""}`}>
          {checkout && checkout.lineItems.length < 1 ? (
            <EmptyCart />
          ) : (
            <>
              <div>
                {/* {isFreeShipping(checkout.totalPrice) ? (
                  <div className={s.Cart__callOut}>
                    <h5>
                      {`You're £${freeShippingAmount(
                        checkout.totalPrice,
                      )} away from
                      free shipping*`}
                    </h5>

                    <Cta
                      title="Continue Shopping"
                      link="/shop"
                      onClick={() => {
                        trackCustomEvent({
                          category: "Cart",
                          action: "Shipping Upsell Click",
                        })
                        dispatch(
                          setState({
                            sidebarIsVisible: false,
                          }),
                        )
                      }}
                    />
                  </div>
                ) : (
                  <div className={s.Cart__callOut}>
                    <h5>You've qualified for free shipping* - get in!</h5>
                    <Link
                      className={s.Cart__callOutTAC}
                      to="/delivery-and-returns"
                    >
                      * Not applicable to Highlands, Islands and Northern
                      Ireland
                    </Link>
                  </div>
                )} */}
                <div className={s.Cart__sustainabilityCallOut}>
                  <div className={s.Cart__sustainabilityCallOut___leaf}>
                    <img src={LeafImage} alt="Image of a leaf" />
                  </div>
                  <h5 className={s.Cart__sustainabilityCallOut___text}>
                    We are offsetting the carbon impact of this delivery
                  </h5>
                  <Cta title="Find out more" link="/sustainability" />
                </div>

                {checkout.lineItems.map((item, i) => {
                  return (
                    <div className={s.CartItem} key={i}>
                      <div
                        className={s.CartItem__image}
                        style={{
                          backgroundImage: `url(${item.variant.image.src})`,
                        }}
                      />

                      <div className={s.CartItem__details}>
                        <h4>{item.title}</h4>

                        {item.variant.selectedOptions.map(option => {
                          if (option.name === "Title") return
                          return <span key={option.value}>{option.value}</span>
                        })}
                        {item.customAttributes.length > 0 && (
                          <span>
                            <i>{item.customAttributes[0].value}</i>
                          </span>
                        )}
                      </div>
                      <div className={s.CartItem__options}>
                        <h4 className={s.CartItem__price}>
                          {item.variant.compareAtPrice ? (
                            <>
                              <span className={s.CartItem__pricePercent}>
                                {`${formatShopifyPrice(
                                  item.variant.priceV2.amount,
                                )} % off`}
                              </span>
                              <span className={s.CartItem__priceCurrent}>
                                {formatShopifyPrice(
                                  item.variant.priceV2.amount,
                                )}
                              </span>
                            </>
                          ) : (
                            <>
                              {formatShopifyPrice(item.variant.priceV2.amount)}
                            </>
                          )}
                        </h4>

                        <QuantitySelector
                          quantity={item.quantity}
                          updateQuantity={val => {
                            dispatch(
                              updateLineItem(
                                checkout.id,
                                item.id,
                                item.quantity + val,
                              ),
                            )
                          }}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
              <div>
                {checkout && checkout.lineItems.length > 0 && (
                  <div className={`${s.Cart__18check}`}>
                    <div>
                      <h4
                        onClick={() => {
                          setIsOver18(true)
                        }}
                        className={
                          isOver18Error ? s.Cart__18check___errorText : null
                        }
                      >
                        Please check the box to confirm you are over the age of
                        18
                      </h4>
                      <p>
                        Sale of alcohol is prohibited to those aged under 18 and
                        we reserve the right to cancel your order if no ID is
                        present at delivery. Full T&Cs{" "}
                        <Link to="terms-and-conditions">here</Link>.
                      </p>
                    </div>
                    <CheckBox
                      className={`${s.Cart__18check__box}`}
                      value={isOver18}
                      onChange={val => setIsOver18(val)}
                      error={isOver18Error}
                    />
                  </div>
                )}
                <div className={s.CartTotals}>
                  <div className={s.CartTotals__row}>
                    <p>Subtotal</p>
                    <h5>{`£${toFixedAmount(checkout.paymentDue)}`}</h5>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {checkout && checkout.lineItems.length > 0 && (
        <div className={`${s.Cart__buttons} ${s.Cart__buttons___double}`}>
          <div>
            <Cta
              title="Continue Shopping"
              className={s.Cart__button___secondary}
              onClick={() => {
                dispatch(
                  setState({
                    sidebarIsVisible: false,
                  }),
                )
              }}
            />
          </div>
          <div>
            <Cta title="Checkout" onClick={openUpSell} />
          </div>
        </div>
      )}

      <div
        className={`${s.Transition} ${
          checkoutTransition && s.Transition___transitioning
        }`}
      ></div>
    </>
  )
}

const EmptyCart = () => {
  const dispatch = useDispatch()
  return (
    <div className={s.Cart__empty}>
      <div className={s.Cart__empty__content}>
        <CartIcon />
        <p>Hmm... it looks like you haven’t added anything to your cart yet.</p>
      </div>

      <div className={s.Cart__buttons}>
        <div>
          <Cta
            title="Shop"
            link="/shop"
            onClick={() => {
              dispatch(
                setState({
                  sidebarIsVisible: false,
                }),
              )
            }}
          />
        </div>
      </div>
    </div>
  )
}

export default Cart
